import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import NumericInput from 'components/shared/inputs/NumericInput';
import React, { useCallback, useState } from 'react';

import { UnitLayouts, OutdoorSpaces, RentTypes, ResidentialLeaseStatusTypes } from 'types/underwriting/income-approach';

type AddMultipleUnitsModalProps = {
  open: boolean;
  maxUnitNumber: number;
  onClose: () => void;
  onAdd: (unitMixes: any[]) => void;
};

export type UnitMix = {
  numberOfUnits: number;
  bedrooms?: number;
  bathrooms?: number;
  squareFeet?: number;
  rent?: number;
  rentType?: RentTypes;
  leaseStatus?: ResidentialLeaseStatusTypes;
  unitType?: UnitLayouts;
  outdoorSpace?: OutdoorSpaces;
};

const initialUnitMixes: UnitMix[] = [
  {
    numberOfUnits: 1,
  },
];

export const AddMultipleUnitsModal: React.FC<AddMultipleUnitsModalProps> = ({
  open,
  maxUnitNumber,
  onClose,
  onAdd,
}) => {
  const [unitMixes, setUnitMixes] = useState<UnitMix[]>(initialUnitMixes);

  const handleAdd = () => {
    onAdd(unitMixes);
    setUnitMixes(initialUnitMixes);
  };

  const handleCancel = () => {
    onClose();
    setUnitMixes(initialUnitMixes);
  };

  const addUnitMix = useCallback(() => {
    setUnitMixes((prev) => [
      ...prev,
      {
        numberOfUnits: 1,
      },
    ]);
  }, []);

  const onUnitMixChange = useCallback(
    (unitMixUpdate: any, index: number) => {
      const updatedUnitMixes = [...unitMixes];
      updatedUnitMixes[index] = { ...updatedUnitMixes[index], ...unitMixUpdate };
      setUnitMixes(updatedUnitMixes);
    },
    [setUnitMixes, unitMixes]
  );

  const onIncrementUnitsNumberClick = useCallback(
    (index: number) => {
      const updatedUnitMixes = [...unitMixes];
      updatedUnitMixes[index] = {
        ...updatedUnitMixes[index],
        numberOfUnits: updatedUnitMixes[index].numberOfUnits + 1,
      };
      setUnitMixes(updatedUnitMixes);
    },
    [setUnitMixes, unitMixes]
  );

  const onDecrementUnitsNumberClick = useCallback(
    (index: number) => {
      const updatedUnitMixes = [...unitMixes];
      updatedUnitMixes[index] = {
        ...updatedUnitMixes[index],
        numberOfUnits: updatedUnitMixes[index].numberOfUnits - 1,
      };
      setUnitMixes(updatedUnitMixes);
    },
    [setUnitMixes, unitMixes]
  );

  const totalUnitsNumber = unitMixes.reduce((acc, curr) => acc + (curr.numberOfUnits || 0), 0);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xl" fullWidth>
      <DialogTitle>Add Multiple Units</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }} direction="column">
          {unitMixes.map((unitMix, index) => (
            <Grid item container direction="row" spacing={2} key={index} alignItems="center">
              <Grid item>
                <NumericInput
                  size="small"
                  value={unitMix.bedrooms ?? 0}
                  label="Bedrooms"
                  sx={{ width: '90px' }}
                  onChange={(newValue) => onUnitMixChange({ bedrooms: newValue }, index)}
                  allowNegative={false}
                />
              </Grid>
              <Grid item>
                <NumericInput
                  size="small"
                  value={unitMix.bathrooms ?? 0}
                  label="Bathrooms"
                  sx={{ width: '90px' }}
                  onChange={(newValue) => onUnitMixChange({ bathrooms: newValue }, index)}
                  allowNegative={false}
                />
              </Grid>
              <Grid item>
                <NumericInput
                  size="small"
                  value={unitMix.squareFeet ?? 0}
                  label="Square feet"
                  sx={{ width: '120px' }}
                  onChange={(newValue) => onUnitMixChange({ squareFeet: newValue }, index)}
                  allowNegative={false}
                />
              </Grid>
              <Grid item>
                <NumericInput
                  size="small"
                  value={unitMix.rent ?? 0}
                  label="Rent"
                  sx={{ width: '120px' }}
                  onChange={(newValue) => onUnitMixChange({ rent: newValue }, index)}
                  allowNegative={false}
                  decimalScale={2}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel id="rent-type-select-label">Rent Type</InputLabel>
                  <Select
                    size="small"
                    labelId="rent-type-select-label"
                    label="Rent Type"
                    sx={{ minWidth: '200px' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    }}
                    onChange={(event) => onUnitMixChange({ rentType: event.target.value }, index)}
                  >
                    {Object.values(RentTypes).map((rentType) => (
                      <MenuItem key={rentType} value={rentType}>
                        {rentType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel id="lease-status-select-label">Lease Status</InputLabel>
                  <Select
                    size="small"
                    labelId="lease-status-select-label"
                    label="Lease Status"
                    sx={{ minWidth: '200px' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    }}
                    onChange={(event) => onUnitMixChange({ leaseStatus: event.target.value }, index)}
                  >
                    {Object.values(ResidentialLeaseStatusTypes).map((leaseStatus) => (
                      <MenuItem key={leaseStatus} value={leaseStatus}>
                        {leaseStatus}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel id="unit-type-select-label">Unit Type</InputLabel>
                  <Select
                    size="small"
                    labelId="unit-type-select-label"
                    label="Unit Type"
                    sx={{ minWidth: '200px' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    }}
                    onChange={(event) => onUnitMixChange({ unitType: event.target.value }, index)}
                  >
                    {Object.values(UnitLayouts).map((unitLayout) => (
                      <MenuItem key={unitLayout} value={unitLayout}>
                        {unitLayout}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel id="outdoor-space-select-label">Outdoor Space</InputLabel>
                  <Select
                    size="small"
                    labelId="outdoor-space-select-label"
                    label="Outdoor Space"
                    sx={{ minWidth: '200px' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    }}
                    onChange={(event) => onUnitMixChange({ outdoorSpace: event.target.value }, index)}
                  >
                    {Object.values(OutdoorSpaces).map((outdoorSpace) => (
                      <MenuItem key={outdoorSpace} value={outdoorSpace}>
                        {outdoorSpace}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <ButtonGroup size="small" aria-label="small outlined button group">
                  <Button
                    disabled={totalUnitsNumber >= maxUnitNumber}
                    onClick={() => onIncrementUnitsNumberClick(index)}
                    color="success"
                  >
                    +
                  </Button>
                  <NumericInput
                    error={totalUnitsNumber > maxUnitNumber}
                    size="small"
                    value={unitMix.numberOfUnits ?? 0}
                    sx={{ width: '50px' }}
                    onChange={(newValue) => onUnitMixChange({ numberOfUnits: newValue }, index)}
                  />
                  {
                    <Button
                      disabled={!unitMix.numberOfUnits || unitMix.numberOfUnits <= 0}
                      onClick={() => onDecrementUnitsNumberClick(index)}
                      color="error"
                    >
                      -
                    </Button>
                  }
                </ButtonGroup>
              </Grid>
            </Grid>
          ))}
          <Grid item>
            <Button variant="outlined" onClick={addUnitMix}>
              + Unit Mix
            </Button>
          </Grid>
          {totalUnitsNumber > maxUnitNumber && (
            <Grid item>
              <Alert severity="error">{`Total units exceed the maximum limit of ${maxUnitNumber}`}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button disabled={totalUnitsNumber > maxUnitNumber} onClick={handleAdd} variant="contained" color="primary">
          Add Units
        </Button>
      </DialogActions>
    </Dialog>
  );
};
